import { useState, useEffect } from 'react';
import { UserPlus, Edit2, Trash2, X, Search } from 'lucide-react';  
import './Clients.css';
import API_URL from '../config';
import jsPDF from 'jspdf';

function Clients() {
  const [mostrarFormulari, setMostrarFormulari] = useState(false); // Controla la visibilitat del formulari
  const [llistaClients, setLlistaClients] = useState([]); // Llista de clients
  const [carregant, setCarregant] = useState(true); // Estat de càrrega
  const [error, setError] = useState(''); // Missatge d'error general
  const [errorFormulari, setErrorFormulari] = useState(''); // Missatge d'error del formulari
  const [nom, setNom] = useState(''); // Camps del formulari de nom
  const [email, setEmail] = useState(''); // Camps del formulari de email
  const [telefon, setTelefon] = useState(''); // Camps del formulari de telèfon
  const [objectiu, setObjectiu] = useState('SALUT'); // Per defecte 'SALUT'
  const [clientEditar, setClientEditar] = useState(null); // Client que s'està editant
  const [cerca, setCerca] = useState('');  // Text de cerca
  const [clientDetall, setClientDetall] = useState(null); // Client seleccionat per veure el detall
  const [mostrarModalDetall, setMostrarModalDetall] = useState(false); // Controla la visibilitat de la fitxa
  const [dadesEconomiques, setDadesEconomiques] = useState(null);
  const [editantDataNaixement, setEditantDataNaixement] = useState('');
  const [editantPes, setEditantPes] = useState('');
  const [editantAlcada, setEditantAlcada] = useState('');
  const [mostrarSeleccioPaquet, setMostrarSeleccioPaquet] = useState(false);
  
  const carregarClients = async () => { // Carrega la llista de clients
      const token = sessionStorage.getItem('token'); // Agafa el token d'autenticació
      
      if (!token) {
        setError('No estàs autenticat. Si us plau, fes login.');
        setCarregant(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/gestio/clients/`, { // Preguntem al backend
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json(); // Obtenim la llista de clients en format JSON
          setLlistaClients(data);
          setError('');
        } else if (response.status === 401 || response.status === 403) {
          setError(`Autenticació fallida (${response.status}). Torna a fer login.`); // Error d'autenticació
        } else {
          const errorData = await response.text();
          setError(`Error ${response.status}: ${errorData.substring(0, 100)}`); // Altres errors
        }
      } catch (err) {
        setError(`Error de connexió: ${err.message}`); // Error de connexió
      } finally {
        setCarregant(false); // Finalitza l'estat de càrrega
      }
    };
  
  useEffect(() => { 
    carregarClients(); // Crida la funció per carregar els clients quan el component es monta
  }, []);

  // Cercar clients
  const clientsFiltrats = llistaClients.filter(client => { // Filtra segons el text de cerca
    const textCerca = cerca.toLowerCase(); // Convertim el text de cerca a minúscules
    return ( // Comprovem si el nom, email o telèfon conté el text de cerca
      client.nom_complet.toLowerCase().includes(textCerca) ||
      client.email.toLowerCase().includes(textCerca) ||
      client.telefon.toLowerCase().includes(textCerca)
    );
  });

  const handleGuardar = async (e) => { // Gestiona la creació o edició d'un client
    e.preventDefault(); // Prevé el comportament per defecte del formulari
    setErrorFormulari(''); // Reseteja els errors del formulari
    
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      setErrorFormulari('No estàs autenticat.');
      return;
    }

    const dadesClient = { 
      nom_complet: nom,
      email,
      telefon,
      objectiu: objectiu
    };

    try {
      if (clientEditar) {
        const response = await fetch(`${API_URL}/api/gestio/clients/${clientEditar.id}/editar/`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify(dadesClient)
        });

        if (response.ok) {
          const clientActualitzat = await response.json(); // Obté el client actualitzat
          setLlistaClients(llistaClients.map(client => 
            client.id === clientEditar.id ? clientActualitzat : client // Actualitza la llista amb el client editat
          ));
          tancarFormulari();
        } else {
          const errorData = await response.json();
          processarErrors(errorData);
        }
      } else {
        const response = await fetch(`${API_URL}/api/gestio/clients/`, { // Creació d'un nou client
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify(dadesClient)
        });

        if (response.ok) {
          const clientCreat = await response.json();
          setLlistaClients([...llistaClients, clientCreat]); // Afegeix el nou client a la llista
          tancarFormulari();
        } else {
          const errorData = await response.json();
          processarErrors(errorData);
        }
      }
    } catch (err) {
      setErrorFormulari(`Error de connexió: ${err.message}`);
    }
  };

  const processarErrors = (errorData) => { // Processa els errors retornats pel backend del formulari
    if (errorData.telefon) {
      setErrorFormulari(`Telèfon: ${errorData.telefon[0]}`);
    } else if (errorData.email) {
      setErrorFormulari(`Email: ${errorData.email[0]}`);
    } else if (errorData.nom_complet) {
      setErrorFormulari(`Nom: ${errorData.nom_complet[0]}`);
    } else {
      setErrorFormulari('Error al guardar el client. Revisa les dades.');
    }
  };

  const obrirFormulariEditar = (client) => { // Obre el formulari amb les dades del client a editar
    setClientEditar(client);
    setNom(client.nom_complet);
    setEmail(client.email);
    setTelefon(client.telefon);
    setErrorFormulari('');
    setMostrarFormulari(true);
  };

  const handleEsborrar = async (id) => {
    if (!window.confirm('Estàs segur que vols esborrar aquest client?')) {
      return;
    }

    const token = sessionStorage.getItem('token');
    
    if (!token) {
      setError('No estàs autenticat.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gestio/clients/${id}/esborrar/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });

      if (response.ok) {
        setLlistaClients(llistaClients.filter(client => client.id !== id));
      } else {
        setError('Error al esborrar el client.');
      }
    } catch (err) {
      setError(`Error de connexió: ${err.message}`);
    }
  };

  const tancarFormulari = () => {
    setMostrarFormulari(false);
    setErrorFormulari('');
    setNom('');
    setEmail('');
    setTelefon('');
    setClientEditar(null);
  };

  const obrirDetall = async (client) => {
    setMostrarSeleccioPaquet(false);
    setClientDetall(client);
    setMostrarModalDetall(true);
    setDadesEconomiques(null);
    setEditantDataNaixement(client.data_naixement || '');
    setEditantPes(client.pes_inicial || '');
    setEditantAlcada(client.alcada || '');

    const token = sessionStorage.getItem('token');
      try {
        const response = await fetch(`${API_URL}/api/gestio/clients/${client.id}/rendibilitat/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDadesEconomiques(data);
        }
      } catch (err) {
        console.error(err);
      }
      };


  const guardarDadesSalut = async () => {
    const token = sessionStorage.getItem('token');
    
    const dadesAPISalut = {
      nom_complet: clientDetall.nom_complet,
      email: clientDetall.email,
      telefon: clientDetall.telefon,
      objectiu: clientDetall.objectiu,
      // Si editantDataNaixement és buit, posem null
      data_naixement: editantDataNaixement === "" ? null : editantDataNaixement,
      // Convertim a número, si no es pot, posem null
      alcada: editantAlcada !== "" ? parseInt(editantAlcada) : null,
      pes_inicial: editantPes !== "" ? parseFloat(editantPes) : null
    };

    try {
      const response = await fetch(`${API_URL}/api/gestio/clients/${clientDetall.id}/editar/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify(dadesAPISalut)
      });

      if (response.ok) {
        await carregarClients(); 
        setMostrarModalDetall(false);
        alert("Dades guardades correctament");
      } else {
        const errors = await response.json();
        console.log("Errors del servidor:", errors);
        alert("Error 400: Revisa els logs de la consola o del terminal.");
      }
    } catch (err) {
      console.error("Error en la petició:", err);
    }
  };

  const calcularIMC = (pes, alcada) => {
      if (!pes || !alcada) return { valor: '---', status: 'N/A' };
      const alcadaMetres = alcada / 100;
      const imc = (pes / (alcadaMetres * alcadaMetres)).toFixed(1);
      let status = '';
      if (imc < 18.5) status = 'PES BAIX';
      else if (imc < 25) status = 'NORMAL';
      else if (imc < 30) status = 'SOBREPES';
      else status = 'OBESITAT';
      return { valor: imc, status };
  }


const generarPDF = () => {
  const doc = new jsPDF(); // El PDF s'organitza mitjançant coordenades (x, y) on (0,0) és la cantonada superior esquerra. El valor de x augmenta cap a la dreta i el valor de y augmenta cap avall. Per això, per anar baixant a la pàgina, anirem augmentant el valor de y.
  const margin = 20;
  let y = 30; // Controla la posició vertical a la pàgina

  const nomsObjectius = {
    'SALUT': 'Manteniment i Salut',
    'PERDRE_PES': 'Perdre Pes',
    'GUANYAR_MUSCUL': 'Guanyar Massa Muscular',
    'REHABILITACIO': 'Rehabilitació',
    'ALTRE': 'Altre'
  };

  const objectiuNet = nomsObjectius[clientDetall.objectiu] || clientDetall.objectiu;
  const imcInfo = calcularIMC(editantPes, editantAlcada);

  // DISSENY I CAPÇALERA 
  // Faig una barra blava lateral per pur disseny
  doc.setFillColor(79, 172, 254);
  doc.rect(0, 0, 10, 300, 'F');

  // Títol principal en negreta i majúscules
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(26, 32, 44);
  doc.text("INFORME DE SEGUIMENT", margin, y);
  
  // Afegeixo la data actual de l'informe
  y += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(113, 128, 150);
  doc.text(`GENERAT EL ${new Date().toLocaleDateString().toUpperCase()}`, margin, y);

  y += 15;

  // BLOC DE DADES PERSONALS 
  // Dibuixo un rectangle de fons gris fluix per agrupar les dades
  doc.setFillColor(247, 250, 252);
  doc.rect(margin, y, 170, 52, 'F');
  
  y += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(79, 172, 254);
  doc.text("DADES DEL CLIENT", margin + 5, y);

  // Llistat de dades personals (majúscules per estètica)
  y += 8;
  doc.setFontSize(10);
  doc.setTextColor(45, 55, 72);
  doc.setFont("helvetica", "bold");
  doc.text("NOM:", margin + 5, y);
  doc.setFont("helvetica", "normal");
  doc.text(clientDetall.nom_complet.toUpperCase(), margin + 40, y);

  y += 7;
  doc.setFont("helvetica", "bold");
  doc.text("EMAIL:", margin + 5, y);
  doc.setFont("helvetica", "normal");
  doc.text(clientDetall.email.toLowerCase(), margin + 40, y);

  y += 7;
  doc.setFont("helvetica", "bold");
  doc.text("TELÈFON:", margin + 5, y);
  doc.setFont("helvetica", "normal");
  doc.text(clientDetall.telefon || '---', margin + 40, y);

  y += 7;
  doc.setFont("helvetica", "bold");
  doc.text("OBJECTIU:", margin + 5, y);
  doc.setFont("helvetica", "normal");
  doc.text(objectiuNet.toUpperCase(), margin + 40, y);

  y += 20;

  // BLOC DE SALUT 
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(79, 172, 254);
  doc.text("PERFIL FÍSIC I SALUT", margin, y);
  
  // Faig una línia horitzontal de color blau
  y += 3;
  doc.setDrawColor(79, 172, 254);
  doc.setLineWidth(0.5);
  doc.line(margin, y, 190, y);

  y += 10;
  doc.setFontSize(10);
  doc.setTextColor(45, 55, 72);
  // Mostro la data, el pes i alcada que hem agafat dels inputs de l'estat
  doc.text(`DATA NAIXEMENT:  ${editantDataNaixement || '---'}`, margin, y);
  y += 8;
  doc.text(`PES ACTUAL:      ${editantPes ? editantPes + ' KG' : '---'}`, margin, y);
  y += 8;
  doc.text(`ALÇADA:          ${editantAlcada ? editantAlcada + ' CM' : '---'}`, margin, y);

  y += 6;
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, 80, 12, 'F');
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(imcInfo.status === 'NORMAL' ? "#38a169" : "#e53e3e");
  doc.text(`ÍNDEX IMC: ${imcInfo.valor} (${imcInfo.status})`, margin + 4, y);

  y += 20;

  // BLOC DE RENDIBILITAT (Només si el backend ens ha enviat dades)
  if (dadesEconomiques) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(79, 172, 254);
    doc.text("ANÀLISI DE RENDIBILITAT", margin, y);
    
    y += 3;
    doc.line(margin, y, 190, y);

    y += 10;
    // Poso 3 caixes de colors per als ingressos, hores i preu/hora
    
    // Ingressos
    doc.setFillColor(248, 255, 248);
    doc.rect(margin, y, 50, 20, 'F');
    doc.setTextColor(26, 32, 44);
    doc.setFontSize(8);
    doc.text("INGRESSOS", margin + 5, y + 7);
    doc.setFontSize(12);
    doc.text(`${dadesEconomiques.ingressos_totals}€`, margin + 5, y + 15);

    // Hores
    doc.setFillColor(247, 250, 252);
    doc.rect(margin + 60, y, 50, 20, 'F');
    doc.setFontSize(8);
    doc.text("HORES", margin + 65, y + 7);
    doc.setFontSize(12);
    doc.text(`${dadesEconomiques.hores_totals}h`, margin + 65, y + 15);

    // Rendibilitat (Canvia el color del text segons si és bona o mala)
    doc.setFillColor(240, 255, 255);
    doc.rect(margin + 120, y, 50, 20, 'F');
    doc.setFontSize(8);
    doc.text("PREU/HORA REAL", margin + 125, y + 7);
    doc.setFontSize(12);
    // Condició per posar el text en verd (si guanyo prou) o vermell (si perdo diners) depèn de si la rendibilitat per hora és superior o inferior a la tarifa interna
    doc.setTextColor(dadesEconomiques.rendibilitat_hora >= dadesEconomiques.tarifa_interna ? "#38a169" : "#e53e3e");
    doc.text(`${dadesEconomiques.rendibilitat_hora}€/h`, margin + 125, y + 15);
  }

  // PEU DE PÀGINA
  doc.setFontSize(8);
  doc.setTextColor(160, 174, 192);
  doc.text("Aquest document és confidencial i per a ús exclusiu de l'entrenador i el client.", margin, 285);
  doc.text("© 2026 PLAFATORMA FITANALYTICS - TFG", 145, 285);

  // Genero el nom del fitxer: INFORME_NOM_DEL_CLIENT.pdf (substituint espais per guions baixos, aixo ho fa el replace)
  const nomArxiu = clientDetall.nom_complet.replace(/\s+/g, '_').toUpperCase();
  doc.save(`INFORME_${nomArxiu}.pdf`);
};

const generarFacturaPDF = (paquet) => {
  const doc = new jsPDF();
  const margin = 20;
  
  // Dades del negoci 
  const nomNegoci = dadesEconomiques.nom_negoci || "FitAnalytics";
  const numFactura = `FACT-${new Date().getFullYear()}-${paquet.id}`;

  // CAPÇALERA
  doc.setFillColor(240, 240, 240);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  doc.text(nomNegoci.toUpperCase(), margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("DOCUMENT DE FACTURACIÓ", 140, 25);

  // DADES FACTURA I CLIENT 
  let y = 55;
  doc.setFont("helvetica", "bold");
  doc.text("FACTURAT A:", margin, y);
  doc.text("DETALLS FACTURA:", 120, y);

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.text(clientDetall.nom_complet.toUpperCase(), margin, y);
  doc.text(`NÚM: ${numFactura}`, 120, y);

  y += 7;
  doc.text(clientDetall.email, margin, y);
  doc.text(`DATA: ${new Date(paquet.data_compra).toLocaleDateString()}`, 120, y);

  // TAULA DE CONCEPTES 
  y += 20;
  doc.setFillColor(45, 55, 72); 
  doc.rect(margin, y, 170, 10, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("CONCEPTE", margin + 5, y + 7);
  doc.text("PREU TOTAL", 160, y + 7);

  y += 20;
  doc.setTextColor(40, 40, 40);
  doc.setFont("helvetica", "normal");
  doc.text(paquet.nom_paquet, margin + 5, y);
  doc.text(`${paquet.preu.toFixed(2)}€`, 160, y);

  // Línia de separació
  y += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, 190, y);

  // PEU DE PÀGINA 
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Gràcies per la teva confiança.", margin, 270);
  doc.text("Aquest document serveix com a comprovant de pagament.", margin, 275);

  // Guardar
  const nomArxiu = `Factura_${clientDetall.nom_complet.replace(/\s+/g, '_')}_${paquet.id}.pdf`;
  doc.save(nomArxiu);
};



  return (
    <div className="seccio-clients">
      <div className="capçalera-llista">
        <div className="text-llista">
          <h1>Gestió de Clients</h1>
          <p>Dades reals connectades amb el teu servidor.</p>
        </div>
        <button className="boto-afegir" onClick={() => setMostrarFormulari(true)}>
          <UserPlus size={18} /> Afegir Client
        </button>
      </div>

      {/* Cercador*/}
      <div className="contenidor-cerca">
        <div className="input-cerca">
          <Search size={18} className="icona-cerca" />
          <input 
            type="text"
            placeholder="Cerca per nom, email o telèfon..."
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            className="camp-cerca"
          />
          {cerca && (
            <button 
              className="boto-netejar-cerca" 
              onClick={() => setCerca('')}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="taula-contenidor">
        {error && (
          <div className="missatge-error">
            {error}
          </div>
        )}

        {carregant ? (
          <p>Carregant dades...</p>
        ) : clientsFiltrats.length === 0 ? (
          <p className="missatge-buit">
            {cerca 
              ? `No s'han trobat clients amb "${cerca}"`
              : 'No tens cap client encara. Afegeix-ne un!'}
          </p>
        ) : (
          <table className="taula-personalitzada">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Telèfon</th>
                <th>Accions</th>
              </tr>
            </thead>
            <tbody>
              {clientsFiltrats.map(client => (
                <tr key={client.id}>
                  <td><strong>{client.nom_complet}</strong></td>
                  <td>{client.email}</td>
                  <td>{client.telefon}</td>
                  <td className="accions">
                    <button className="icon-btn" onClick={() => obrirFormulariEditar(client)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="icon-btn vermell" onClick={() => handleEsborrar(client.id)}>
                      <Trash2 size={16} />
                    </button>
                    <button 
                      className="boto-detall" 
                      onClick={() => obrirDetall(client)}
                    >
                      Detall
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {mostrarFormulari && (
        <div className="Formulari-overlay">
          <div className="Formulari-contingut">
            <div className="Formulari-header">
              <h3>{clientEditar ? 'Editar client' : 'Nou client'}</h3> {/* Mostra el títol segons l'acció */}
              <button className="boto-tancar" onClick={tancarFormulari}>
                <X size={20} />
              </button>
            </div>
            
            <form className="formulari-client" onSubmit={handleGuardar}>
              {errorFormulari && (
                <div className="error-formulari">
                  {errorFormulari}
                </div>
              )}
              
              <div className="camp">
                <label>Nom Complet</label>
                <input 
                  type="text" 
                  value={nom} 
                  onChange={(e) => setNom(e.target.value)} 
                  required 
                />
              </div>
              <div className="camp">
                <label>Correu Electrònic</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
              <div className="camp">
                <label>Telèfon</label>
                <input 
                  type="tel" 
                  value={telefon} 
                  onChange={(e) => setTelefon(e.target.value)} 
                  required 
                />
              </div>

              <div className="camp">
                <label>Objectiu del client</label>
                <select 
                  value={objectiu} 
                  onChange={(e) => setObjectiu(e.target.value)}
                  className="select-form"
                  required
                >
                  <option value="SALUT">Manteniment i Salut</option>
                  <option value="PERDRE_PES">Perdre Pes</option>
                  <option value="GUANYAR_MUSCUL">Guanyar Massa Muscular</option>
                  <option value="REHABILITACIO">Rehabilitació</option>
                  <option value="ALTRE">Altre</option>
                </select>
              </div>
              <div className="Formulari-accions">
                <button 
                  type="button" 
                  className="boto-cancelar" 
                  onClick={tancarFormulari}
                >
                  Cancel·lar
                </button>
                <button type="submit" className="boto-guardar">
                  {clientEditar ? 'Actualitzar' : 'Guardar a la BD'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mostrarModalDetall && clientDetall && (
        <div className="modal-overlay">
          <div className="modal-contingut detall-modal">
            <div className="Formulari-header">
              <h3>Fitxa de client: {clientDetall.nom_complet}</h3>
              <button className="boto-tancar" onClick={() => setMostrarModalDetall(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="detall-contingut">
              {/* SECCIÓ SALUT */}
              <div className="detall-seccio">
                <h4 className="titol-seccio">Perfil físic i salut</h4>
                <p className={`subtitol-imc ${calcularIMC(editantPes, editantAlcada).status === 'NORMAL' ? 'imc-normal' : 'imc-risc'}`}>
                  IMC: {calcularIMC(editantPes, editantAlcada).valor} ({calcularIMC(editantPes, editantAlcada).status})
                </p>
                <div className="detall-grid">
                  <div className="detall-item">
                    <span className="label">Data naixement:</span>
                    <input 
                      type="date" 
                      className="input-detall"
                      value={editantDataNaixement} 
                      onChange={(e) => setEditantDataNaixement(e.target.value)}
                    />
                  </div>
                  <div className="detall-item">
                    <span className="label">Alçada (cm):</span>
                    <input type="number" 
                    className="input-detall" 
                    value={editantAlcada} 
                    onChange={(e) => setEditantAlcada(e.target.value)} />
                  </div>
                  <div className="detall-item">
                    <span className="label">Pes actual (kg):</span>
                    <input 
                      type="number" 
                      step="0.1"
                      className="input-detall"
                      value={editantPes} 
                      onChange={(e) => setEditantPes(e.target.value)}
                    />
                  </div>
                  <div className="detall-item">
                    <span className="label">Objectiu:</span> {clientDetall.objectiu}
                  </div>
                </div>
                <button className="boto-actualitzar-perfil" onClick={guardarDadesSalut}>
                  Guardar canvis de salut
                </button>
              </div>

              {/* SECCIÓ RENDIBILITAT */}
              <div className="detall-seccio">
                <h4 className="titol-seccio">Anàlisi de rendibilitat (Business Intelligence)</h4>
                
                {dadesEconomiques ? (
                  !mostrarSeleccioPaquet ? (
                    /* VISTA NORMAL DEL RESUM */
                    <div className="detall-grid">
                      <div className="detall-item">
                        <span className="label">Ingressos totals:</span> {dadesEconomiques.ingressos_totals}€
                      </div>
                      <div className="detall-item">
                        <span className="label">Hores dedicades:</span> {dadesEconomiques.hores_totals}h
                      </div>
                      <div className="detall-item">
                        <span className="label">Preu/Hora real:</span> 
                        <span className={`valor-rendibilitat ${dadesEconomiques.rendibilitat_hora >= dadesEconomiques.tarifa_interna ? 'positiu' : 'negatiu'}`}>
                          {dadesEconomiques.rendibilitat_hora}€/h
                        </span>
                      </div>
                      <div className="detall-item">
                        <span className="label">Paquets contractats:</span> {dadesEconomiques.num_paquets}
                        <button 
                          className="boto-factura-link" 
                          onClick={() => setMostrarSeleccioPaquet(true)}
                        >
                          Generar Factura
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* VISTA DE SELECCIÓ DE PAQUET PER A LA FACTURA */
                    <div className="selector-factures">
                      <div className="header-selector">
                        <p className="instruccio-factura">Selecciona el paquet per a la factura:</p>
                        <button className="boto-tornar" onClick={() => setMostrarSeleccioPaquet(false)}>
                          Tornar al resum
                        </button>
                      </div>
                      
                      <ul className="llista-paquets-factura">
                        {dadesEconomiques.detall_paquets?.map((paquet) => (
                          <li key={paquet.id} className="item-paquet-factura">
                            <span>{paquet.nom_paquet} ({new Date(paquet.data_compra).toLocaleDateString()})</span>
                            <strong>{paquet.preu}€</strong>
                            <button className="boto-pdf-mini" onClick={() => generarFacturaPDF(paquet)}>
                              PDF
                            </button>
                          </li>
                        ))}
                        {(!dadesEconomiques.detall_paquets || dadesEconomiques.detall_paquets.length === 0) && (
                          <p className="properament">No hi ha detalls de paquets disponibles.</p>
                        )}
                      </ul>
                    </div>
                  )
                ) : (
                  <p className="properament">Carregant dades econòmiques...</p>
                )}
              </div>

              {/* BOTÓ PDF */}
              <div className="Formulari-accions">
                <button 
                  className="boto-guardar" 
                  onClick={generarPDF}
                >
                  Descarregar Informe PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
